import { useState, useEffect, useCallback } from 'react';

import { getBrowserWindow, type StorageEventLike } from './browser';

/**
 * A hook that syncs state with localStorage.
 * Works like useState but persists the value to localStorage.
 *
 * @param key - The localStorage key to use
 * @param initialValue - The initial value (used if no value exists in storage)
 * @returns A tuple of [value, setValue] like useState
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Get initial value from localStorage or use the provided initial value
  const [storedValue, setStoredValue] = useState<T>(() => {
    const browser = getBrowserWindow();
    if (!browser?.localStorage) {
      return initialValue;
    }
    try {
      const item = browser.localStorage.getItem(key);
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Persist to localStorage whenever the value changes
  useEffect(() => {
    const browser = getBrowserWindow();
    if (!browser?.localStorage) {
      return;
    }
    try {
      browser.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  // Listen for changes in other tabs/windows
  useEffect(() => {
    const browser = getBrowserWindow();
    if (!browser?.addEventListener || !browser?.removeEventListener) {
      return;
    }

    const handleStorageChange = (event: StorageEventLike) => {
      if (!event) return;
      const { key: eventKey, newValue } = event;
      if (eventKey === key && newValue !== null && newValue !== undefined) {
        try {
          setStoredValue(JSON.parse(newValue) as T);
        } catch (error) {
          console.warn(`Error parsing localStorage change for key "${key}":`, error);
        }
      }
    };

    browser.addEventListener('storage', handleStorageChange);
    return () => browser.removeEventListener?.('storage', handleStorageChange);
  }, [key]);

  // Stable setter that supports functional updates
  const setValue = useCallback((value: T | ((prev: T) => T)) => {
    setStoredValue((prev) => {
      const nextValue = value instanceof Function ? value(prev) : value;
      return nextValue;
    });
  }, []);

  return [storedValue, setValue];
}
